import {
  CreateLocalTrackOptions as TwilioCreateLocalTrackOptions,
  LocalAudioTrack as TwilioLocalAudioTrack,
  LocalParticipant as TwilioLocalParticipant,
  LocalVideoTrack as TwilioLocalVideoTrack,
} from "twilio-video";
import { logger } from "../../../datadog/logger";
import { sendLoggingEvents, TrackingEvents } from "../../../events";
import {
  AudioVideoSettings,
  BackgroundSettings,
  LocalAVTrackContextAtom,
  LocalMediaStatus,
} from "../../core/types";
import { getOrCreateLocalMediaTrack } from "./getOrCreateLocalMediaTracks";
import { TrackOperationResult } from "./types";
import { publishLocalTrack, trackKindToMutexMap } from "./utils";

export type PublishTrackOptions = {
  trackOptions?: TwilioCreateLocalTrackOptions;
  backgroundOptions?: BackgroundSettings;
  trackState?: LocalMediaStatus;
  track?: TwilioLocalAudioTrack | TwilioLocalVideoTrack;
};

export type TrackKind = "audio" | "video";

/**
 * Publish the local track for either audio or video, adding it to the room
 */
export async function publishLocalAudioVideoTrack(
  trackKind: TrackKind,
  getLocalTrack: () => LocalAVTrackContextAtom,
  options: PublishTrackOptions = {},
  setStoredAudioVideoSettings: (
    updater: (prev: AudioVideoSettings) => AudioVideoSettings,
  ) => void,
  localParticipant?: TwilioLocalParticipant,
): Promise<TrackOperationResult> {
  const release = await trackKindToMutexMap[trackKind]?.acquire();
  try {
    const {
      trackOptions,
      track: givenTrack,
      trackState: givenTrackState,
    } = options;
    const trackState = givenTrackState ?? LocalMediaStatus.READY;
    const shouldEnable = trackState === LocalMediaStatus.READY;

    let track = givenTrack;
    if (!givenTrack) {
      const createLocalTrackResult = await getOrCreateLocalMediaTrack(
        trackKind,
        getLocalTrack,
        setStoredAudioVideoSettings,
        localParticipant,
        trackOptions ?? {},
      );
      track = createLocalTrackResult.track;
    }

    if (!track) {
      return {
        success: false,
      };
    }
    await publishLocalTrack(track, localParticipant, shouldEnable);

    let deviceName: string | null = null;

    if (trackKind === "audio") {
      const audioTrack = track as TwilioLocalAudioTrack;
      deviceName =
        audioTrack.noiseCancellation?.sourceTrack?.label ??
        audioTrack?.mediaStreamTrack?.label ??
        null;
    } else {
      deviceName = track?.mediaStreamTrack?.label ?? null;
    }

    sendLoggingEvents(TrackingEvents.LOCAL_TRACK_PUBLISHED, {
      trackKind,
      trackState,
      deviceName,
    });

    return {
      success: true,
      status: trackState,
      track,
    };
  } catch (error) {
    logger.error(
      `Unable to publish local ${trackKind} track`,
      options,
      error as Error,
    );
    return {
      success: false,
      status: LocalMediaStatus.ERROR,
    };
  } finally {
    release?.();
  }
}
